import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";
import { Loader2 } from "lucide-react";

const ResetPassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessingToken, setIsProcessingToken] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const appUrl = import.meta.env.VITE_APP_URL || window.location.origin;
  const loginPath = "/login";

  // Process the token on component mount
  useEffect(() => {
    const processToken = async () => {
      try {
        console.log("ResetPassword: Processing token on component mount");
        console.log("ResetPassword: Current URL:", window.location.href);
        
        // Safety timeout to ensure we don't get stuck in loading state
        const safetyTimeoutId = setTimeout(() => {
          if (isProcessingToken) {
            console.log("ResetPassword: Safety timeout triggered, forcing exit from loading state");
            setIsProcessingToken(false);
          }
        }, 5000);
        
        // Extract token from URL if present
        const extractTokenFromUrl = () => {
          // Check for token in hash fragment
          const hash = window.location.hash;
          const urlParams = new URLSearchParams(window.location.search);
          const hashParams = new URLSearchParams(hash.replace('#/reset-password?', ''));
          
          // Try to get access_token from different possible locations
          let accessToken = urlParams.get('access_token') || 
                           hashParams.get('access_token') ||
                           null;
          
          // If token is in the hash but not properly parsed by URLSearchParams
          if (!accessToken && hash.includes('access_token=')) {
            const tokenMatch = hash.match(/access_token=([^&]+)/);
            if (tokenMatch && tokenMatch[1]) {
              accessToken = tokenMatch[1];
            }
          }
          
          console.log("ResetPassword: Access token found:", accessToken ? "Yes" : "No");
          
          // Also extract other token-related parameters
          let refreshToken = urlParams.get('refresh_token') || 
                            hashParams.get('refresh_token') ||
                            '';
                            
          if (!refreshToken && hash.includes('refresh_token=')) {
            const refreshMatch = hash.match(/refresh_token=([^&]+)/);
            if (refreshMatch && refreshMatch[1]) {
              refreshToken = refreshMatch[1];
            }
          }
          
          return { accessToken, refreshToken };
        };
        
        const { accessToken, refreshToken } = extractTokenFromUrl();
        
        if (accessToken) {
          console.log("ResetPassword: Attempting to set session with token");
          
          try {
            // Try to set the session with the token
            const { error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            });
            
            if (error) {
              console.error("ResetPassword: Error setting session:", error);
            } else {
              console.log("ResetPassword: Session set successfully");
            }
          } catch (sessionError) {
            console.error("ResetPassword: Error in setSession:", sessionError);
          }
        }
        
        // Wait a moment for Supabase to process the token
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Check if we have a session after Supabase processes the token
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session) {
          console.log("ResetPassword: Valid session found, user can reset password");
        } else {
          console.log("ResetPassword: No session found, but proceeding with password reset form");
          
          // If no session but we have a token, try to use it directly
          if (accessToken) {
            console.log("ResetPassword: No session but token exists, will try to use token directly for password reset");
          }
        }
        
        // Show the password reset form regardless
        setIsProcessingToken(false);
        clearTimeout(safetyTimeoutId);
        
      } catch (err) {
        console.error("ResetPassword: Error processing token:", err);
        setError("An unexpected error occurred. Please try again.");
        setIsProcessingToken(false);
      }
    };
    
    processToken();
  }, []);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("ResetPassword: Form submitted, validating passwords");
    
    if (password !== confirmPassword) {
      console.log("ResetPassword: Passwords do not match");
      setError("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      console.log("ResetPassword: Password too short");
      setError("Password must be at least 6 characters");
      return;
    }

    console.log("ResetPassword: Password validation passed, setting loading state");
    setIsLoading(true);
    setError(null);

    try {
      console.log("ResetPassword: Starting password update in Supabase Auth");
      
      // Try to update the password directly
      const { data, error } = await supabase.auth.updateUser({
        password: password,
      });

      if (error) {
        console.error("ResetPassword: Error updating password:", error);
        throw error;
      }

      console.log("ResetPassword: Password updated successfully", data);
      
      // Make sure to set loading to false
      setIsLoading(false);
      
      // Show success message
      toast({
        title: "Success!",
        description: "Your password has been updated successfully.",
        variant: "default",
      });
      
      // Also show an alert for guaranteed visibility
      alert("Password updated successfully! You will now be redirected to the login page.");
      
      // Redirect to login page after a short delay
      setTimeout(() => {
        window.location.href = `${appUrl}${loginPath}`;
      }, 2000);
      
    } catch (error) {
      console.error("ResetPassword: Error in password reset:", error);
      setError(error instanceof Error ? error.message : "Failed to reset password");
      setIsLoading(false);
    }
  };

  if (isProcessingToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-medium text-center">Reset Password</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center justify-center space-y-4 py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-center text-sm text-gray-500">
              Processing your password reset request...
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-medium text-center">Reset Password</CardTitle>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4 text-sm">
              {error}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Input
                type="password"
                placeholder="New Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
                autoComplete="new-password"
              />
            </div>
            <div className="space-y-2">
              <Input
                type="password"
                placeholder="Confirm New Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={isLoading}
                autoComplete="new-password"
              />
            </div>
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Updating...
                </>
              ) : (
                "Reset Password"
              )}
            </Button>
            <div className="text-center">
              <Button
                variant="link"
                className="text-sm"
                onClick={() => navigate("/login")}
                type="button"
                disabled={isLoading}
              >
                Back to Login
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;